import type { ReactNode } from 'react';
import type { Row, RowSelectionState } from '@tanstack/react-table';

/**
 * Props for the SelectColumn component.
 *
 * Supports two modes:
 * - **Controlled**: Use `value` + `onChange` - parent owns the state
 * - **Uncontrolled**: Use `initial` + optional `onSelectionChange` - table owns the state
 *
 * Note: Row IDs are derived from the `getRowId` prop on the parent `<DataTable>` component.
 */
export interface SelectColumnProps<TData = unknown> {
  /**
   * Controlled mode: Current selection state.
   * When provided, the table syncs to this value on every render.
   * Parent is the source of truth.
   *
   * @example
   * ```tsx
   * const [selection, setSelection] = useState<RowSelectionState>({});
   * <DataTable.SelectColumn value={selection} onChange={setSelection} />
   * ```
   */
  value?: RowSelectionState;

  /**
   * Uncontrolled mode: Initial selection state applied on mount.
   * Only used when `value` is not provided.
   * Table manages its own state after mount.
   *
   * @example
   * ```tsx
   * <DataTable.SelectColumn initial={{ 'row-1': true }} />
   * ```
   */
  initial?: RowSelectionState;

  /**
   * Called when selection changes.
   * Receives the raw RowSelectionState object.
   * Works in both controlled and uncontrolled modes.
   *
   * @example Controlled mode
   * ```tsx
   * <DataTable.SelectColumn value={selection} onChange={setSelection} />
   * ```
   *
   * @example Uncontrolled with callback
   * ```tsx
   * <DataTable.SelectColumn onChange={(sel) => console.log('Selected:', sel)} />
   * ```
   */
  onChange?: (selection: RowSelectionState) => void;

  /** Enable multi-select (default: true) */
  enableMultiSelect?: boolean;

  /**
   * Controls the header checkbox behavior for bulk selection.
   *
   * - `'all'` — Full select-all checkbox (default)
   * - `'deselect-only'` — Disabled when empty, indeterminate when selected, always deselects
   * - `'none'` — No header checkbox
   *
   * @default 'all'
   */
  selectAllMode?: 'all' | 'deselect-only' | 'none';

  /**
   * Per-row selection control. Return false to prevent a row from being selected.
   * When provided, overrides the default all-or-nothing enableRowSelection boolean.
   *
   * @example
   * ```tsx
   * <DataTable.SelectColumn
   *   enableRowSelection={(row) => row.status !== 'locked'}
   * />
   * ```
   */
  enableRowSelection?: (row: TData) => boolean;

  /**
   * Tooltip shown on the checkbox when a row cannot be selected.
   * Can be a static string or a function that receives the row data and returns a tooltip string.
   *
   * @example
   * ```tsx
   * <DataTable.SelectColumn
   *   disabledTooltip="This device is faulted"
   *   // or
   *   disabledTooltip={(row) => `Cannot select: ${row.reason}`}
   * />
   * ```
   */
  disabledTooltip?: string | ((row: TData) => string | undefined);

  /**
   * Enable sorting by selection state. When enabled, the select column can
   * participate in TanStack sorting.
   *
   * The sort indicator is **not** shown on the checkbox header because it
   * uses a `customHeaderRenderer`.
   *
   * @default false
   */
  sortable?: boolean;

  /**
   * Custom sorting function for the select column.
   * Works exactly like `sortingFn` on `DataTable.Column`.
   *
   * @example Sort selected rows before unselected
   * ```tsx
   * <DataTable.SelectColumn
   *   sortable
   *   sortingFn={(rowA, rowB) => {
   *     const a = rowA.getIsSelected() ? 0 : 1;
   *     const b = rowB.getIsSelected() ? 0 : 1;
   *     return a - b;
   *   }}
   * />
   * ```
   */
  sortingFn?: string | ((rowA: Row<TData>, rowB: Row<TData>, columnId: string) => number);
}

/**
 * SelectColumn component for enabling row selection.
 *
 * This is a declarative/config component that returns null.
 * Selection configuration is extracted by parseChildren and used
 * to configure TanStack Table's row selection feature.
 *
 * **Important:** Provide `getRowId` on the parent `<DataTable>` to ensure stable row IDs.
 *
 * Supports two modes:
 * - **Controlled**: Use `value` + `onChange` - parent owns the state
 * - **Uncontrolled**: Omit `value`, optionally use `onChange` to observe changes
 *
 * @example Uncontrolled (table owns state)
 * ```tsx
 * <DataTable id="users" data={users} getRowId={(row) => row.id}>
 *   <DataTable.SelectColumn
 *     onChange={(selection) => console.log('Selected:', Object.keys(selection))}
 *   />
 * </DataTable>
 * ```
 *
 * @example Controlled (parent owns state)
 * ```tsx
 * const [selection, setSelection] = useState<RowSelectionState>({});
 *
 * <DataTable id="users" data={users} getRowId={(row) => row.id}>
 *   <DataTable.SelectColumn
 *     value={selection}
 *     onChange={setSelection}
 *   />
 * </DataTable>
 * ```
 *
 * @example Single selection only
 * ```tsx
 * <DataTable.SelectColumn
 *   value={selection}
 *   onChange={setSelection}
 *   enableMultiSelect={false}
 *   selectAllMode="none"
 * />
 * ```
 */
export const SelectColumn = <TData,>(props: SelectColumnProps<TData>): ReactNode => {
  if (import.meta.env.DEV) {
    if (props.value !== undefined && props.initial !== undefined) {
      console.warn(
        'DataTable.SelectColumn: `value` and `initial` are mutually exclusive. ' +
          'Use `value` for controlled mode or `initial` for uncontrolled mode, not both.'
      );
    }
    if (props.value !== undefined && props.onChange === undefined) {
      console.warn(
        'DataTable.SelectColumn: Using `value` without `onChange` makes the selection state read-only. ' +
          'User interactions will not update selection. Provide an `onChange` handler.'
      );
    }
  }
  // Pure declarative component - configuration is parsed by parseChildren
  return null;
};

SelectColumn.displayName = 'DataTable.SelectColumn';
